/** Các chủ đề hướng dẫn user có thể chọn trong chế độ "Hướng dẫn sử dụng" của chatbot. */
export type GuideTopic = 'documentation' | 'acceptance' | 'bug-report' | 'feature-request' | 'project-detail';

export const GUIDE_TOPIC_OPTIONS: Record<GuideTopic, { label: string; description: string }> = {
  documentation: {
    label: 'Xem tài liệu',
    description: 'Hướng dẫn sử dụng các tính năng chính',
  },
  acceptance: {
    label: 'Nghiệm thu chức năng',
    description: 'Hướng dẫn quy trình nghiệm thu từng bước',
  },
  'bug-report': {
    label: 'Báo lỗi',
    description: 'Hướng dẫn cách báo cáo lỗi chi tiết',
  },
  'feature-request': {
    label: 'Tạo yêu cầu chức năng',
    description: 'Hướng dẫn cách đề xuất tính năng mới',
  },
  'project-detail': {
    label: 'Xem chi tiết dự án',
    description: 'Hướng dẫn sử dụng các tính năng chính của dự án',
  },
};

/** Một bước hướng dẫn: mô tả bằng lời, kèm ảnh minh hoạ tuỳ chọn (ảnh chụp màn hình thao tác tương ứng). */
export interface GuideStep {
  text: string;
  /**
   * Đường dẫn ảnh tĩnh, bắt đầu bằng "/" — đặt file trong
   * `apps/web/public/guide-images/` rồi tham chiếu dạng
   * `/guide-images/ten-file.png`. Bỏ trống nếu bước đó không cần ảnh.
   */
  image?: string;
}

/**
 * Nội dung hướng dẫn thuần văn bản (+ ảnh tĩnh tuỳ chọn) — mỗi chủ đề là một
 * danh sách bước, bot gửi lần lượt từng bước một dưới dạng tin nhắn (có nút
 * Tiếp theo/Quay lại). Không gắn với bất kỳ phần tử DOM nào của trang thật —
 * an toàn tuyệt đối, không có rủi ro xung đột với overlay/Portal/dialog như
 * cách làm bằng spotlight (driver.js) trước đây.
 */
export const GUIDE_TOPIC_STEPS: Record<GuideTopic, GuideStep[]> = {
  documentation: [
    {
      text: 'Bên trái màn hình là menu chính: "Nghiệm thu" để kiểm tra tính năng, "Lỗi (bug)" để xem báo cáo lỗi, "Yêu cầu chức năng" để xem đề xuất tính năng mới.',
      image: '/guide-images/documentation-01-sidebar.png',
    },
    {
      text: 'Góc trên bên phải có ô chọn Dự án (khi đang ở màn hình liên quan) — mỗi dự án có danh sách tính năng riêng.',
      image: '/guide-images/documentation-02-project-selector.png',
    },
    {
      text: 'Bấm vào avatar ở góc trên bên phải để xem tài khoản, đổi mật khẩu, hoặc đăng xuất.',
      image: '/guide-images/documentation-03-user-menu.png',
    },
    {
      text: 'Nút tròn có hình robot ở góc màn hình chính là tôi — bấm vào bất cứ lúc nào để hỏi thêm hoặc xem lại hướng dẫn này.',
    },
  ],
  acceptance: [
    {
      text: 'Vào menu "Dự án" ở bên trái, chọn một dự án, rồi bấm "Nghiệm thu dự án" để vào màn hình nghiệm thu.',
      image: '/guide-images/acceptance-01-open.png',
    },
    {
      text: 'Bên trái màn hình nghiệm thu là cây tính năng theo từng giai đoạn — bấm mở rộng một giai đoạn rồi chọn tính năng muốn xem.',
      image: '/guide-images/acceptance-02-feature-tree.png',
    },
    {
      text: 'Đọc mô tả chi tiết và xem ảnh minh hoạ của tính năng để hiểu rõ yêu cầu trước khi quyết định.',
      image: '/guide-images/acceptance-03-feature-detail.png',
    },
    {
      text: 'Chọn "Đã xác nhận" nếu tính năng hoạt động đúng như mong đợi, hoặc "Yêu cầu thay đổi" nếu còn cần chỉnh sửa (nhớ ghi rõ lý do). Lưu ý: nếu tính năng đang còn lỗi (bug) chưa xử lý xong, bạn sẽ không gửi được xác nhận — phải chờ lỗi được khắc phục trước.',
      image: '/guide-images/acceptance-04-choice.png',
    },
    {
      text: 'Bấm nút "Gửi" để hoàn tất — kết quả nghiệm thu sẽ được lưu lại và đội phát triển sẽ nhận được thông báo.',
      image: '/guide-images/acceptance-05-submit.png',
    },
  ],
  'bug-report': [
    {
      text: 'Vào menu "Lỗi (bug)" ở bên trái, rồi bấm nút "Thêm lỗi" ở góc trên bên phải để mở form báo lỗi mới.',
      image: '/guide-images/bug-report-01-nav.png',
    },
    {
      text: 'Chọn Dự án, rồi Giai đoạn để hiện danh sách tính năng — sau đó bấm vào tính năng đang gặp lỗi.',
      image: '/guide-images/bug-report-02-pick-feature.png',
    },
    {
      text: 'Viết tiêu đề ngắn gọn mô tả lỗi, ví dụ: "Nút Lưu bị vô hiệu hoá sau khi bấm 2 lần liên tiếp".',
      image: '/guide-images/bug-report-03-title.png',
    },
    {
      text: 'Mô tả chi tiết: lỗi xảy ra khi nào, các bước để tái hiện lại, kết quả hiện tại so với kết quả bạn mong đợi.',
      image: '/guide-images/bug-report-04-description.png',
    },
    {
      text: 'Chọn độ nghiêm trọng phù hợp: Nghiêm trọng (hệ thống không dùng được), Cao (một chức năng bị hỏng), Trung bình (ảnh hưởng trải nghiệm), Thấp (chi tiết nhỏ, không gấp).',
      image: '/guide-images/bug-report-05-severity.png',
    },
    {
      text: 'Đính kèm ảnh chụp màn hình hoặc video nếu có — rất hữu ích để xác định đúng vị trí lỗi.',
      image: '/guide-images/bug-report-06-images.png',
    },
    {
      text: 'Bấm "Lưu" để gửi báo cáo. Vậy là xong — cảm ơn bạn đã giúp cải thiện sản phẩm!',
      image: '/guide-images/bug-report-07-submit.png',
    },
  ],
  'feature-request': [
    {
      text: 'Vào menu "Yêu cầu chức năng" ở bên trái để xem danh sách các đề xuất đã tạo trước đó.',
      image: '/guide-images/feature-request-01-nav.png',
    },
    {
      text: 'Bấm nút "Tạo yêu cầu" ở góc trên bên phải để mở form tạo đề xuất mới (hoặc chat trực tiếp với tôi như bây giờ, cũng là một cách!). Đặt tên ngắn gọn, ví dụ: "Thêm tìm kiếm nâng cao" hoặc "Hỗ trợ chế độ tối".',
      image: '/guide-images/feature-request-02-form.png',
    },
    {
      text: 'Mô tả chi tiết: tính năng này là gì, tại sao cần, ai sẽ dùng, và lợi ích mang lại.',
      image: '/guide-images/feature-request-03-description.png',
    },
    {
      text: 'Có thể chọn Dự án và Giai đoạn liên quan nếu đã biết — không bắt buộc.',
      image: '/guide-images/feature-request-04-project-phase.png',
    },
    {
      text: 'Đính kèm hình ảnh, ảnh chụp màn hình, hoặc bản thiết kế minh hoạ nếu có — giúp đội phát triển hiểu rõ ý tưởng hơn.',
      image: '/guide-images/feature-request-05-attachment.png',
    },
    {
      text: 'Gửi đi — đội phát triển sẽ xem xét và phản hồi đề xuất của bạn.',
      image: '/guide-images/feature-request-06-submit.png',
    },
  ],
  'project-detail': [
    {
      text: 'Vào menu "Dự án" ở bên trái, rồi bấm "Chi tiết" trên thẻ dự án muốn xem.',
      image: '/guide-images/project-detail-01-open.png',
    },
    {
      text: 'Đầu trang là thông tin chung (tên dự án, mô tả). Phần "Tài liệu" liệt kê các tài liệu, hướng dẫn, và file liên quan tới dự án — chia theo "Tài liệu chung" và theo từng giai đoạn.',
      image: '/guide-images/project-detail-02-documents.png',
    },
    {
      text: 'Bấm vào một tài liệu để xem trước (nếu hỗ trợ) hoặc tải xuống.',
      image: '/guide-images/project-detail-03-preview.png',
    },
    {
      text: 'Nếu cần đề xuất thêm tính năng cho dự án này, bấm nút "Tạo yêu cầu chức năng" ngay trên trang.',
      image: '/guide-images/project-detail-04-create-feature.png',
    },
  ],
};
